import { Typography, Box, CircularProgress } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { getMarketDataByCategory } from "../../../api/api"; // adjust path as needed

const CATEGORY_FILTERS = [
  { label: "Equity", slug: "indexes" },
  { label: "Bonds", slug: "bonds" },
  { label: "Commodities", slug: "commodities" },
  { label: "Countries", slug: "countries" },
  { label: "Currencies", slug: "currencies" },
  { label: "Cryptocurrency", slug: "cryptocurrency" },

];

const TABLE_HEADERS = [
  { label: "Name", key: "name" },
  { label: "Symbol", key: "symbol" },
  //   { label: "Type", key: "instrument_type" },
  { label: "Price", key: "current_price" },
  { label: "Day Low", key: "day_low" },
  { label: "Day High", key: "day_high" },
  { label: "52W Low", key: "week_52_low" },
  { label: "52W High", key: "week_52_high" },
  { label: "Volume", key: "volume" },
  { label: "Daily Chg", key: "change_today_pct" },
  { label: "5 Days", key: "change_5d_pct" },
  { label: "1 Month", key: "change_1m_pct" },
  { label: "YTD", key: "change_ytd_pct" },
  { label: "1 Year", key: "change_1y_pct" },
  { label: "3 Years", key: "change_3y_pct" },
];

const formatPrice = (value) => {
  if (value === null || value === undefined) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  const sign = num >= 0 ? "+" : "";
  return `${sign}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

const formatVolume = (value) => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (isNaN(num)) return "—";
  if (num >= 1_000_000) return `${(num / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;
  if (num >= 1_000) return `${(num / 1_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}K`;
  return num.toLocaleString();
};

const PERCENT_KEYS = [
  "change_today_pct",
  "change_5d_pct",
  "change_1m_pct",
  "change_ytd_pct",
  "change_1y_pct",
  "change_3y_pct",
];

const PRICE_KEYS = [
  "current_price",
  "day_low",
  "day_high",
  "week_52_low",
  "week_52_high",
];

const getCellDisplay = (instrument, key) => {
  const value = instrument[key];

  if (key === "name") {
    return {
      text: value,
      color: "var(--color-text-primary)",
      fontWeight: "var(--font-weight-semibold)",
    };
  }

  if (key === "symbol") {
    return {
      text: value,
      color: "var(--color-primary)",
      fontWeight: "var(--font-weight-semibold)",
    };
  }

  if (key === "instrument_type") {
    return {
      text: value || "—",
      color: "var(--color-text-secondary)",
      fontWeight: "var(--font-weight-medium)",
    };
  }

  if (PERCENT_KEYS.includes(key)) {
    const display = formatPercent(value);
    let color = "var(--color-text-muted)";
    if (value !== null && value !== undefined) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        color =
          num > 0
            ? "var(--color-success)"
            : num < 0
              ? "var(--color-error)"
              : "var(--color-text-muted)";
      }
    }
    return {
      text: display,
      color,
      fontWeight: "var(--font-weight-semibold)",
    };
  }

  if (PRICE_KEYS.includes(key)) {
    return {
      text: formatPrice(value),
      color: "var(--color-text-primary)",
      fontWeight: "var(--font-weight-medium)",
    };
  }

  if (key === "volume") {
    return {
      text: formatVolume(value),
      color: "var(--color-text-secondary)",
      fontWeight: "var(--font-weight-medium)",
    };
  }

  return {
    text: value ?? "—",
    color: "var(--color-text-muted)",
    fontWeight: "var(--font-weight-medium)",
  };
};

export default function MarketData() {
  const [activeFilter, setActiveFilter] = useState(0);
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (slug) => {
    setLoading(true);
    setError(null);
    setCategoryData(null);

    const result = await getMarketDataByCategory(slug);

    if (result.authExpired) {
      setError("Session expired. Please log in again.");
    } else if (!result.success) {
      setError("Failed to load market data. Please try again.");
    } else {
      setCategoryData(result.data);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    const slug = CATEGORY_FILTERS[activeFilter]?.slug;
    if (slug) fetchData(slug);
  }, [activeFilter, fetchData]);

  const handleFilterClick = (idx) => {
    if (idx !== activeFilter) {
      setActiveFilter(idx);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "var(--color-bg-primary)",
        px: "var(--spacing-xl)",
        pb: "var(--spacing-2xl)",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          mb: "var(--spacing-lg)",
          mt: "var(--spacing-xl)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-xs)",
        }}
      >
        <Typography variant="title">Market Data</Typography>
        <Typography
          variant="paragraph"
          sx={{ color: "var(--color-text-muted)" }}
        >
          {categoryData?.description ||
            "Track performance across commodities, bonds, and global markets"}
        </Typography>
      </Box>


      {/* ── Filters ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-sm)",
          marginBottom: "var(--spacing-lg)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-xs)",
            overflowX: "auto",
            flex: 1,
            scrollbarWidth: "thin",
            scrollbarColor: "var(--color-border-secondary) transparent",
          }}
        >
          {CATEGORY_FILTERS.map((f, idx) => (
            <button
              key={f.slug}
              onClick={() => handleFilterClick(idx)}
              style={{
                padding: "6px var(--spacing-md)",
                fontSize: "var(--font-size-sm)",
                fontWeight:
                  idx === activeFilter
                    ? "var(--font-weight-semibold)"
                    : "var(--font-weight-medium)",
                border: "1px solid",
                borderColor:
                  idx === activeFilter
                    ? "var(--color-primary)"
                    : "var(--color-border-primary)",
                borderRadius: "var(--radius-sm)",
                whiteSpace: "nowrap",
                transition: "all var(--transition-fast)",
                backgroundColor:
                  idx === activeFilter
                    ? "var(--color-primary)"
                    : "var(--color-surface)",
                color:
                  idx === activeFilter
                    ? "var(--color-text-inverse)"
                    : "var(--color-text-secondary)",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
              onMouseEnter={(e) => {
                if (idx !== activeFilter)
                  e.currentTarget.style.backgroundColor =
                    "var(--color-surface-hover)";
              }}
              onMouseLeave={(e) => {
                if (idx !== activeFilter)
                  e.currentTarget.style.backgroundColor =
                    "var(--color-surface)";
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

      </div>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
        }}
      >
        <Typography sx={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Prices reflect market data as of{" "}
          {categoryData?.last_updated
            ? (() => {
              const date = new Date(categoryData.last_updated);

              const utc = date.toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                timeZone: "UTC",
                timeZoneName: "short",
              });

              const local = date.toLocaleString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                timeZoneName: "short",
              });

              return `${utc} (${local})`;
            })()
            : "—"}
        </Typography>
      </Box>
      {/* ── Loading State ── */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: "var(--spacing-2xl)",
          }}
        >
          <CircularProgress size={36} sx={{ color: "var(--color-primary)" }} />
        </Box>
      )}

      {/* ── Error State ── */}
      {error && !loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: "var(--spacing-2xl)",
            flexDirection: "column",
            gap: "var(--spacing-md)",
          }}
        >
          <Typography
            variant="paragraph"
            sx={{ color: "var(--color-error)", textAlign: "center" }}
          >
            {error}
          </Typography>
          <button
            onClick={() => fetchData(CATEGORY_FILTERS[activeFilter]?.slug)}
            style={{
              padding: "8px var(--spacing-lg)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              backgroundColor: "var(--color-primary)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              color: "var(--color-text-inverse)",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Retry
          </button>
        </Box>
      )}

      {/* ── Sections ── */}
      {!loading && !error && categoryData?.subcategories && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing-lg)",
          }}
        >
          {categoryData.subcategories.map((section) => (
            <div
              key={section.name}
              style={{
                backgroundColor: "var(--color-surface)",
                border: "1px solid var(--color-border-primary)",
                borderRadius: "var(--radius-xl)",
                overflow: "hidden",
              }}
            >
              {/* Section Title */}
              <div
                style={{
                  padding: "var(--spacing-md) var(--spacing-lg)",
                  borderBottom: "1px solid var(--color-border-primary)",
                  background:
                    "linear-gradient(135deg, var(--color-primary-lighter) 0%, transparent 100%)",
                }}
              >
                <Typography
                  variant="paragraph"
                  sx={{ color: "var(--color-text-primary)" }}
                >
                  {section.name}
                </Typography>
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr
                      style={{
                        borderBottom: "1px solid var(--color-border-primary)",
                      }}
                    >
                      {TABLE_HEADERS.map((h) => (
                        <th
                          key={h.key}
                          style={{
                            padding: "var(--spacing-sm) var(--spacing-md)",
                            textAlign: "left",
                            fontSize: "var(--font-size-sm)",
                            fontWeight: "var(--font-weight-bold)",
                            color: "var(--color-text-muted)",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            whiteSpace: "nowrap",
                            fontFamily: "inherit",
                          }}
                        >
                          {h.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {section.instruments.map((instrument, idx) => (
                      <tr
                        key={instrument.symbol}
                        style={{
                          borderBottom:
                            idx !== section.instruments.length - 1
                              ? "1px solid var(--color-border-primary)"
                              : "none",
                          transition:
                            "background-color var(--transition-fast)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "var(--color-surface-hover)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor =
                            "transparent";
                        }}
                      >
                        {TABLE_HEADERS.map((h) => {
                          const cell = getCellDisplay(instrument, h.key);
                          return (
                            <td
                              key={h.key}
                              style={{
                                padding:
                                  "var(--spacing-sm) var(--spacing-md)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Typography
                                variant="paragraph"
                                sx={{
                                  fontWeight: cell.fontWeight,
                                  color: cell.color,
                                }}
                              >
                                {cell.text}
                              </Typography>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty State ── */}
      {!loading &&
        !error &&
        categoryData?.subcategories?.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: "var(--spacing-2xl)",
            }}
          >
            <Typography
              variant="paragraph"
              sx={{ color: "var(--color-text-muted)" }}
            >
              No data available for this category.
            </Typography>
          </Box>
        )}
    </Box>
  );
}